import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { useLanguageCatalogue } from '../lib/languages';
import { ErrorBox, Loading, Notice, StatusBadge } from '../components/ui';

interface PlannedLesson {
  id: string;
  slug: string;
  title: string;
  english_title: string | null;
  brief: string | null;
  track: string;
  level: number;
  game_type: string;
  review_status: string;
  ai_generated: boolean;
  written: boolean;
  content: { items?: { word: string; gloss: string; picture?: string }[] };
}
interface Readiness {
  planned: number;
  written: number;
  approved: number;
  awaiting_review: number;
}

/**
 * The curriculum desk.
 *
 * Every language has the same thirty-topic plan; this is where a native speaker turns a plan
 * into lessons. Each row is one topic with the brief written for it, and two ways to fill it:
 * write it yourself, or let the AI draft something for you to correct. Either way it lands in
 * review and a child sees nothing until a speaker approves it.
 *
 * This screen is the only realistic path to ninety-four languages. Nobody is going to write
 * 2,800 lessons alone.
 */
export function CurriculumEditor() {
  const { catalogue } = useLanguageCatalogue();
  const { user } = useAuth();
  const [lang, setLang] = useState('yo');
  const { data, error, loading, reload } = useApi<{ lessons: PlannedLesson[]; readiness: Readiness }>(`/curriculum?language=${encodeURIComponent(lang)}`);
  const act = useAction();
  const [openId, setOpenId] = useState<string | null>(null);

  // Approving language content and protecting children are different jobs needing
  // different knowledge, so they are different accounts. A safeguarding admin can
  // write a lesson but cannot wave it through; the screen now says so, instead of
  // showing buttons that answer 403.
  const canReview = user?.role === 'content_editor';

  const languages = (catalogue?.regions ?? []).flatMap((r) => r.languages);

  return (
    <section className="card stack">
      <div className="spread">
        <h2 style={{ margin: 0 }}>Write the curriculum</h2>
        <select aria-label="Language" value={lang} onChange={(e) => setLang(e.target.value)}>
          {languages.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
      </div>
      <p className="muted">
        Every language starts from the same thirty topics, and a language may go further than them. Write one in your own words, or let the AI draft it and correct
        what it got wrong — a draft is a starting point, never an answer. Nothing reaches a child until a speaker approves it.
      </p>

      <ErrorBox error={error ?? act.error} />
      <Notice text={act.notice} />
      {loading && <Loading />}

      {data?.readiness && (
        <p className="small muted">
          <strong>{data.readiness.planned}</strong> planned · <strong>{data.readiness.written}</strong> written · <strong>{data.readiness.awaiting_review}</strong>{' '}
          awaiting a speaker's check · <strong>{data.readiness.approved}</strong> approved
        </p>
      )}

      {canReview && (
        <p className="small muted">
          <strong>Approve sits inside each lesson, not on this list.</strong> Open one with “Read and decide” and the buttons are under its words — because
          approving a lesson you have not read is the one thing this screen exists to prevent.
        </p>
      )}

      {!canReview && (
        <p className="small muted">
          You can write and correct lessons here. Approving them is the content editor's job, on the content editor account — a different pair of eyes from the one
          that wrote them.
        </p>
      )}

      <div className="stack tight">
        {data?.lessons.map((l) => (
          <div key={l.id} className="plan-row">
            <div className="stack tight" style={{ minWidth: 0 }}>
              <strong className="block">{l.english_title ?? l.title}</strong>
              {l.brief && <span className="muted small block">{l.brief}</span>}
              {l.written && (
                <span className="row small">
                  <span className="yo">{l.title}</span>
                  <StatusBadge status={l.review_status} />
                  {l.ai_generated && <span className="badge warn">AI draft</span>}
                </span>
              )}
            </div>
            <div className="row">
              {!l.written && <span className="badge">not written</span>}
              <button className="btn small ghost" onClick={() => setOpenId(openId === l.id ? null : l.id)}>
                {openId === l.id ? 'Close' : l.written ? (canReview ? 'Read and decide' : 'Edit') : 'Write'}
              </button>
              {canReview && (
                <button
                  className="btn small secondary"
                  disabled={act.busy}
                  onClick={async () => {
                    const r = await act.run(() => api<{ aiAvailable: boolean; message?: string }>(`/lessons/${l.id}/draft-with-ai`, { body: {} }));
                    if (r && !r.aiAvailable) act.setNotice(r.message ?? 'AI drafting is not configured.');
                    else act.setNotice('Drafted — now read every word and correct it before approving.');
                    reload();
                  }}
                >
                  Draft with AI
                </button>
              )}
            </div>
            {openId === l.id && (
              <div className="plan-write stack">
                <WriteLesson lesson={l} onSaved={() => { setOpenId(null); reload(); }} />
                {canReview && l.written && ['draft', 'native_review'].includes(l.review_status) && (
                  <ReviewLesson lesson={l} onDecided={() => { setOpenId(null); reload(); }} />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function WriteLesson({ lesson, onSaved }: { lesson: PlannedLesson; onSaved: () => void }) {
  const act = useAction();
  const [title, setTitle] = useState(lesson.written ? lesson.title : '');
  const [rows, setRows] = useState(() =>
    lesson.content.items?.length ? lesson.content.items.map((i) => ({ word: i.word, gloss: i.gloss, picture: i.picture ?? '' })) : [{ word: '', gloss: '', picture: '' }],
  );

  const set = (i: number, key: 'word' | 'gloss' | 'picture', v: string) => setRows(rows.map((r, n) => (n === i ? { ...r, [key]: v } : r)));

  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const items = rows.filter((r) => r.word.trim() && r.gloss.trim()).map((r) => ({ word: r.word.trim(), gloss: r.gloss.trim(), picture: r.picture.trim() || undefined }));
        if (!items.length) return act.setError('Add at least one word and what it means.');
        const ok = await act.run(() => api(`/lessons/${lesson.id}/write`, { body: { title: title.trim() || undefined, items } }), 'Saved — waiting for a speaker to check it.');
        if (ok) onSaved();
      }}
    >
      <label className="stack tight">
        <span className="label">What this lesson is called in the language</span>
        <input className="yo" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={lesson.english_title ?? ''} />
      </label>
      <span className="label">The words</span>
      {rows.map((r, i) => (
        <div className="row" key={i}>
          <input className="yo" placeholder="word" value={r.word} onChange={(e) => set(i, 'word', e.target.value)} aria-label={`Word ${i + 1}`} />
          <input placeholder="what it means in English" value={r.gloss} onChange={(e) => set(i, 'gloss', e.target.value)} aria-label={`Meaning ${i + 1}`} />
          <input placeholder="emoji" style={{ maxWidth: '5rem' }} value={r.picture} onChange={(e) => set(i, 'picture', e.target.value)} aria-label={`Picture ${i + 1}`} />
        </div>
      ))}
      <div className="row">
        <button type="button" className="btn small ghost" onClick={() => setRows([...rows, { word: '', gloss: '', picture: '' }])}>
          Add a word
        </button>
        <button className="btn small" disabled={act.busy}>
          Save
        </button>
      </div>
      <ErrorBox error={act.error} />
    </form>
  );
}

/**
 * The decision a child's whole experience rests on.
 *
 * This sits UNDER the lesson's words on purpose: to approve anything you must first open it,
 * so the words are on screen when you decide. There is no approve-all, and there never will
 * be — thirty lessons waved through in one click is exactly the outcome the review pipeline
 * exists to prevent, and it would be an easy button to build and an expensive one to undo.
 *
 * Sending back is not a rejection of the person. It returns the lesson to the desk with a
 * note, and whoever writes it next sees that note.
 */
function ReviewLesson({ lesson, onDecided }: { lesson: PlannedLesson; onDecided: () => void }) {
  const act = useAction();
  const [notes, setNotes] = useState('');
  const [sendingBack, setSendingBack] = useState(false);

  const decide = async (decision: 'approve' | 'reject') => {
    if (decision === 'reject' && !notes.trim()) {
      return act.setError('Say what needs changing. A lesson sent back with no note just comes back the same.');
    }
    const ok = await act.run(
      () => api(`/lessons/${lesson.id}/review`, { body: { decision, notes: notes.trim() || undefined } }),
      decision === 'approve' ? 'Approved. Children learning this language can see it now.' : 'Sent back with your note.',
    );
    if (ok) onDecided();
  };

  return (
    <div className="stack tight review-box">
      <span className="label">Your decision as a speaker</span>
      <p className="small muted" style={{ margin: 0 }}>
        Approving means a child will be taught these words as their family's language. If any of it is wrong, or is a variety this lesson does not say it is, send it
        back instead.
      </p>
      {sendingBack && (
        <label className="stack tight">
          <span className="label">What needs changing</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. the third word is Ọ̀yọ́, not standard — say so, or use the standard word" />
        </label>
      )}
      <div className="row">
        <button type="button" className="btn small" disabled={act.busy} onClick={() => void decide('approve')}>
          Approve
        </button>
        {sendingBack ? (
          <>
            <button type="button" className="btn small secondary" disabled={act.busy} onClick={() => void decide('reject')}>
              Send it back
            </button>
            <button type="button" className="btn small ghost" onClick={() => { setSendingBack(false); setNotes(''); act.setError(null); }}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="btn small ghost" disabled={act.busy} onClick={() => setSendingBack(true)}>
            Send it back
          </button>
        )}
      </div>
      <ErrorBox error={act.error} />
      <Notice text={act.notice} />
    </div>
  );
}
